import React, { memo, useCallback } from 'react'
import { Box, Divider, Flex, Text, Avatar } from '@chakra-ui/core'
import { IConfigRetailUser, ITag, RootState } from 'constants/interfaces'
import { useUserName } from 'hooks/useUserName'
import { CustomCheckBox } from '../GroupsTree/style'
import { useSelector } from 'react-redux'
import TagsPrevew from '../TagsPrevew'
import { SelectionMode } from '../OptimizedGroupsTree/hooks/useSelection'
import { MAX_TAGS_IN_USER } from 'components/AdminConsole/Tags/utils'

const MemoizedAvatar = memo(
  ({ name, src }: { name: string; src?: string }) => {
    return <Avatar size="sm" name={name} src={src} />
  },
  (prevProps, nextProps) => {
    return prevProps.name === nextProps.name && prevProps.src === nextProps.src
  }
)

interface UserRowProps {
  user: IConfigRetailUser
  isSelected: boolean
  toggleSelection: (id: string) => void
  style: React.CSSProperties
  isShowTags: boolean
  selectionMode: SelectionMode
  isLocked?: boolean
}

export const UserRow = memo(
  ({ user, isSelected, toggleSelection, style, isShowTags, selectionMode, isLocked = false }: UserRowProps) => {
    const { mappedGroupsByUsers, tags } = useSelector((state: RootState) => state.config.config)
    const theme = useSelector((state: RootState) => state.general.theme)
    const userName = useUserName(user.uid)
    const handleToggleSelection = useCallback(() => {
      if (isLocked) {
        return
      }

      toggleSelection(user.uid)
    }, [isLocked, user.uid, toggleSelection])

    const userGroup = mappedGroupsByUsers?.[user.uid]?.[0] ?? ''

    let isDisabled = isLocked
    let userTags: ITag[] = []

    if (selectionMode === SelectionMode.RAW) {
      userTags = tags.filter((tag) => tag.users?.includes(user.uid)) || []
      isDisabled = isDisabled || userTags.length > MAX_TAGS_IN_USER
    }

    return (
      <Box style={style} px="15px">
        <Flex alignItems="center" h="60px" justifyContent="space-between">
          <Flex alignItems="center" style={{ columnGap: '10px' }}>
            <MemoizedAvatar name={userName} src={user.profile_img_url} />
            <Flex flex="1" isTruncated w="max-content" maxWidth="250px" flexDir="column" alignItems="flex-start">
              <Text fontSize="17px" fontWeight={600} color="#202426">
                {userName}
              </Text>
              {isShowTags ? (
                <TagsPrevew tags={userTags} visibleCount={3} />
              ) : (
                <Text fontSize="12px" color="gray.500">
                  {userGroup}
                </Text>
              )}
            </Flex>
          </Flex>
          <CustomCheckBox
            aria-label={userName}
            color={theme?.elementsColor}
            isChecked={isSelected}
            onChange={handleToggleSelection}
            id={`user-checkbox-${user.uid}`}
            name={`user-checkbox-${userName}`}
            isDisabled={isDisabled}
          />
        </Flex>
        <Divider my={0} />
      </Box>
    )
  }
)
